import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./OffertoSignPlayer.css";

/**
 * ✅ ROBUST duration parser (kept; may be used elsewhere)
 */
const parseDurationToMs = (durationStr = "") => {
    if (!durationStr) return 0;

    const cleaned = durationStr
        .toString()
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();

    const match = cleaned.match(
        /^(\d+)\s*(minute|minutes|hour|hours|day|days|month|months|year|years)$/
    );

    if (!match) return 0;

    const value = parseInt(match[1], 10);
    const unit = match[2];

    switch (unit) {
        case "minute":
        case "minutes":
            return value * 60 * 1000;
        case "hour":
        case "hours":
            return value * 60 * 60 * 1000;
        case "day":
        case "days":
            return value * 24 * 60 * 60 * 1000;
        case "month":
        case "months":
            return value * 30 * 24 * 60 * 60 * 1000;
        case "year":
        case "years":
            return value * 365 * 24 * 60 * 60 * 1000;
        default:
            return 0;
    }
};

/**
 * ✅ IMPORTANT: These values MUST match server.js keys:
 * offer_to_sign: { "8_minutes"(optional), "1_month", "3_months", "6_months", "12_months" }
 */
const DURATION_OPTIONS = [
    { value: "1_month", label: "1 Month" },
    { value: "3_months", label: "3 Months" },
    { value: "6_months", label: "6 Months" },
    { value: "12_months", label: "1 Year" },
];

const OffertoSignPlayer = () => {
    const { playerId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const playerProfileSlug = location.state?.playerProfileSlug || "";

    const [player, setPlayer] = useState(null);
    const [existingOffers, setExistingOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [submitting, setSubmitting] = useState(false);

    // ✅ Pricing state
    const [pricing, setPricing] = useState({}); // { "1_month": "$10.00", ...}
    const [pricingLoading, setPricingLoading] = useState(true);
    const [pricingError, setPricingError] = useState("");

    // ✅ Selected duration state (for showing fee line)
    const [selectedDuration, setSelectedDuration] = useState("");

    // ✅ Offer-to-sign capacity state (remaining slots)
    const [capacityLoading, setCapacityLoading] = useState(true);
    const [capacityError, setCapacityError] = useState("");
    const [capacity, setCapacity] = useState({
        role: null,
        activeContracts: 0,
        maxCapacity: 0,
        remainingSlots: null, // null until fetched
        averageRating: null,
        ratingCount: 0,
        traceId: null,
    });

    /**
     * ✅ IDENTITY (STATE FIRST, STORAGE FALLBACK)
     */
    const role =
        (location.state?.senderRole ||
            localStorage.getItem("userRole") ||
            localStorage.getItem("role") ||
            "").toLowerCase();

    const senderId = location.state?.senderId || localStorage.getItem(`${role}Id`);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!playerProfileSlug || !role || !senderId) {
                    console.error("❌ Missing identity:", { playerProfileSlug, role, senderId });
                    setError("Missing required identity data.");
                    return;
                }

                const playerRes = await apiFetch(`/api/player-profile/${playerProfileSlug}`);
                if (!playerRes.ok) throw new Error("Failed to load player.");
                const playerData = await playerRes.json();
                setPlayer(playerData);

                const offersRes = await apiFetch(`/api/offers?playerId=${playerData.id}`);
                if (!offersRes.ok) throw new Error("Failed to load offers.");
                const offersData = await offersRes.json();

                const myOffers = (offersData || []).filter((offer) => {
                    const offerRole = (offer.signedByRole || "").toLowerCase();

                    let offerSenderId = null;
                    if (offerRole === "agent") offerSenderId = offer.agentId;
                    if (offerRole === "scout") offerSenderId = offer.scoutId;
                    if (offerRole === "club") offerSenderId = offer.clubId;

                    return offerRole === role && String(offerSenderId) === String(senderId);
                });

                setExistingOffers(myOffers);
            } catch (err) {
                console.error("❌ Failed loading offer page:", err);
                setError("Failed to load player or offer data.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [playerProfileSlug, role, senderId]);

    /**
     * ✅ Fetch pricing from backend
     * Assumes route: /api/pricing/offer-to-sign
     */
    useEffect(() => {
        const fetchPricing = async () => {
            try {
                setPricingLoading(true);
                setPricingError("");

                const res = await apiFetch(`/api/pricing/offer-to-sign`);
                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    throw new Error(text || "Failed to load pricing.");
                }

                const data = await res.json();

                const map = {};
                (data?.prices || []).forEach((p) => {
                    if (p?.key && p?.formatted) map[p.key] = p.formatted;
                });

                setPricing(map);
            } catch (e) {
                console.error("❌ Pricing fetch failed:", e);
                setPricingError("Pricing unavailable right now.");
            } finally {
                setPricingLoading(false);
            }
        };

        fetchPricing();
    }, []);

    /**
     * ✅ Fetch offer-to-sign remaining slots (capacity)
     * Assumes route: GET /api/offer-to-sign-capacity
     */
    useEffect(() => {
        const fetchCapacity = async () => {
            try {
                setCapacityLoading(true);
                setCapacityError("");

                const res = await apiFetch(`/api/offer-to-sign-capacity`);
                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    throw new Error(text || "Failed to load offer-to-sign capacity.");
                }

                const data = await res.json();

                setCapacity({
                    role: data?.role ?? null,
                    activeContracts: Number(data?.activeContracts) || 0,
                    maxCapacity: Number(data?.maxCapacity) || 0,
                    remainingSlots:
                        data?.remainingSlots === 0 || data?.remainingSlots
                            ? Number(data?.remainingSlots)
                            : null,
                    averageRating:
                        data?.averageRating === 0 || data?.averageRating
                            ? Number(data?.averageRating)
                            : null,
                    ratingCount: Number(data?.ratingCount) || 0,
                    traceId: data?.traceId ?? null,
                });
            } catch (e) {
                console.error("❌ Capacity fetch failed:", e);
                setCapacityError("Offer-to-sign capacity unavailable right now.");
            } finally {
                setCapacityLoading(false);
            }
        };

        // Only attempt if user role exists (api requires auth anyway)
        if (role) fetchCapacity();
        else {
            setCapacityLoading(false);
            setCapacityError("Missing role for capacity check.");
        }
    }, [role]);

    /**
     * ✅ Sender-level blocking (restored + fixed):
     * Block sender from submitting another offer for the same player if they have:
     * 1) an in-progress negotiation (pending/accepted with adminApproval null), OR
     * 2) an admin-approved offer that is not yet paid (approved but Unpaid/PendingPayment)
     *
     * NOTE: Backend must also enforce this. This is UI-only protection.
     */
    const hasOpenOfferWithPlayer = useMemo(() => {
        return existingOffers.some((offer) => {
            const status = (offer.status || "").toLowerCase();
            const adminApproval = offer.adminApproval; // null | "approved" | "rejected" | (maybe "closed" depending on your system)
            const paymentStatus = (offer.paymentStatus || "").toLowerCase(); // "unpaid" | "pendingpayment" | "paid"

            // ❌ Never block on completed offers
            if (["closed", "rejected", "terminated", "withdrawn"].includes(status)) {
                return false;
            }

            // ✅ Still negotiating / awaiting admin decision
            if (["pending", "accepted"].includes(status) && adminApproval === null) {
                return true;
            }

            // ✅ Admin already approved this sender's offer, but it's not activated/paid yet
            if (adminApproval === "approved" && paymentStatus !== "paid") {
                return true;
            }

            return false;
        });
    }, [existingOffers]);

    /**
     * ✅ Capacity-level blocking (NEW):
     * If remainingSlots is known and is 0, block submitting.
     * If capacity can't be fetched, we do NOT hard-block (UI warning only).
     */
    const isAtCapacity = useMemo(() => {
        return capacity?.remainingSlots !== null && Number(capacity.remainingSlots) <= 0;
    }, [capacity]);

    const handleOfferSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        try {
            if (!player || !player.id) throw new Error("Player not loaded.");

            // Optional hard block if capacity is confirmed 0
            if (isAtCapacity) {
                alert(
                    "You have no remaining offer-to-sign slots available. Please wait for an active contract to expire or close one before submitting a new offer."
                );
                return;
            }

            setSubmitting(true);

            const formData = new FormData(e.target);
            const durationKey = formData.get("duration"); // "8_minutes" | "1_month" | ...
            const notes = formData.get("notes") || "";
            const agreeToTerms = formData.get("agreeToTerms") === "on";
            const contractFile = formData.get("contractFile");

            if (!durationKey) {
                alert("Please select a contract duration.");
                return;
            }

            if (!agreeToTerms) {
                alert("You must agree to VSN terms.");
                return;
            }

            if (!contractFile || !(contractFile instanceof File) || contractFile.size === 0) {
                alert("Please upload a contract file.");
                return;
            }

            // ✅ UPDATED: No more payment at submission.
            // We still show the user the fee for transparency.
            const feeText = pricing[durationKey];
            const confirmText = feeText
                ? `This will submit your offer for review. If accepted and approved, you will be asked to pay ${feeText} to activate the contract. Continue?`
                : `This will submit your offer for review. If accepted and approved, you will be asked to pay to activate the contract. Continue?`;

            const ok = window.confirm(confirmText);
            if (!ok) return;

            // ✅ Submit offer + upload contract (no Stripe redirect here).
            const fd = new FormData();
            fd.append("playerId", String(player.id));
            fd.append("duration", String(durationKey));
            fd.append("notes", notes);
            fd.append("agreeToTerms", "true");
            fd.append("contractFile", contractFile);

            const submitRes = await apiFetch(`/api/checkout/offer-to-sign`, {
                method: "POST",
                body: fd,
            });

            const submitData = await submitRes.json().catch(() => ({}));

            if (!submitRes.ok) {
                throw new Error(submitData?.error || "Failed to submit offer.");
            }

            // ✅ We no longer require `url` here. We expect a contractId.
            const createdId = submitData?.contractId || submitData?.offerId;
            if (!createdId) {
                console.warn("⚠️ No contractId/offerId returned:", submitData);
            }

            alert("Offer submitted successfully. The player will be notified.");

            // Refresh offers list so sender-level blocking updates immediately
            try {
                const offersRes = await apiFetch(`/api/offers?playerId=${player.id}`);
                if (offersRes.ok) {
                    const offersData = await offersRes.json();
                    const myOffers = (offersData || []).filter((offer) => {
                        const offerRole = (offer.signedByRole || "").toLowerCase();

                        let offerSenderId = null;
                        if (offerRole === "agent") offerSenderId = offer.agentId;
                        if (offerRole === "scout") offerSenderId = offer.scoutId;
                        if (offerRole === "club") offerSenderId = offer.clubId;

                        return offerRole === role && String(offerSenderId) === String(senderId);
                    });
                    setExistingOffers(myOffers);
                }
            } catch (refreshErr) {
                console.warn("⚠️ Offers refresh failed (non-fatal):", refreshErr);
            }

            // Refresh capacity too (so remaining slots updates after submit if backend counts immediately later)
            try {
                const capRes = await apiFetch(`/api/offer-to-sign-capacity`);
                if (capRes.ok) {
                    const capData = await capRes.json().catch(() => ({}));
                    setCapacity((prev) => ({
                        ...prev,
                        role: capData?.role ?? prev.role,
                        activeContracts: Number(capData?.activeContracts) || 0,
                        maxCapacity: Number(capData?.maxCapacity) || 0,
                        remainingSlots:
                            capData?.remainingSlots === 0 || capData?.remainingSlots
                                ? Number(capData?.remainingSlots)
                                : prev.remainingSlots,
                        averageRating:
                            capData?.averageRating === 0 || capData?.averageRating
                                ? Number(capData?.averageRating)
                                : prev.averageRating,
                        ratingCount: Number(capData?.ratingCount) || 0,
                        traceId: capData?.traceId ?? prev.traceId,
                    }));
                }
            } catch (capRefreshErr) {
                console.warn("⚠️ Capacity refresh failed (non-fatal):", capRefreshErr);
            }
        } catch (err) {
            console.error("❌ Offer submit failed:", err);
            alert(err.message || "Failed to submit offer.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <p className="loading">Loading...</p>;
    if (error) return <p className="error">{error}</p>;
    if (!player) return <p className="error">Player not found.</p>;

    const selectedFee = selectedDuration ? pricing[selectedDuration] : "";

    // ✅ Build remaining-slots label for display (same place as Activation fee)
    const remainingSlotsText = capacityLoading
        ? "Loading..."
        : capacityError
            ? "—"
            : capacity?.remainingSlots === null
                ? "—"
                : String(capacity.remainingSlots);

    return (
        <div className="offer-sign-container">
            <h2>
                {role.charAt(0).toUpperCase() + role.slice(1)} Offer for {player.fullName}
            </h2>

            <div className="vsn-policy-note">
                <p className="note">
                    <strong>Note:</strong> Submit your contract offer for the player. Your proposal should outline the opportunities, trials, and representation you intend to provide during the selected contract duration.If this offer is accepted and approved, the selected contract
                    duration defines how long this player will be exclusively represented by you on VSN.
                    During the active contract, you may take the player on trials, assess performance, and
                    attempt to secure opportunities. Other agents, scouts, or clubs will not be allowed to
                    make signing offers once the contract is activated.
                </p>

                <a
                    href="/VSN_Representation_Policy.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vsn-policy-link"
                >
                    📄 Read VSN’s official representation & contract policy
                </a>
            </div>

            {hasOpenOfferWithPlayer ? (
                <div className="vsn-warning-box">
                    <p>
                        You already have an offer in progress for this player (pending review or awaiting
                        activation). Please complete activation or wait until the offer is closed.
                    </p>
                </div>
            ) : isAtCapacity ? (
                <div className="vsn-warning-box">
                    <p>
                        You have <strong>0</strong> remaining offer-to-sign slots. Please wait for an active
                        contract to expire or close one before submitting a new offer.
                    </p>
                </div>
            ) : (
                <form onSubmit={handleOfferSubmit} className="form">
                    <div className="form-group">
                        <label>Contract Duration:</label>
                        <select
                            name="duration"
                            required
                            value={selectedDuration}
                            onChange={(e) => setSelectedDuration(e.target.value)}
                        >
                            <option value="">Select duration</option>

                            {DURATION_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                    {pricing[opt.value] ? ` — ${pricing[opt.value]}` : ""}
                                </option>
                            ))}
                        </select>

                        {selectedDuration && (
                            <div style={{ marginTop: "8px", fontSize: "14px" }}>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "20px",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        marginTop: "8px",
                                    }}
                                >
                                    <span>
                                        Activation fee:{" "}
                                        <strong>{pricingLoading ? "Loading..." : selectedFee || "—"}</strong>
                                    </span>

                                    <span>
                                        Remaining Slots:{" "}
                                        <strong>
                                            {`You can only sign ${remainingSlotsText} ${Number(remainingSlotsText) === 1 ? "more player" : "more players"
                                                }`}
                                        </strong>{" "}
                                        <span style={{ opacity: 0.7, fontSize: "13px" }}>
                                            (limit increases after 3 positive player ratings)
                                        </span>
                                    </span>
                                </div>
                            </div>
                        )}

                        {pricingError && (
                            <div style={{ marginTop: "8px", fontSize: "14px" }}>{pricingError}</div>
                        )}

                        {capacityError && (
                            <div style={{ marginTop: "8px", fontSize: "14px" }}>{capacityError}</div>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Additional Notes for the Player:</label>
                                <textarea name="notes" rows={1} className="additional-notes" />
                    </div>

                    <div className="form-group">
                        <label>Upload Contract:</label>
                        <input type="file" name="contractFile" required />
                    </div>

                    <div className="form-group">
                        <label>
                            <input type="checkbox" name="agreeToTerms" required /> I agree to VSN terms
                        </label>
                    </div>

                    <button type="submit" disabled={submitting}>
                        {submitting ? "Submitting..." : "Submit Offer"}
                    </button>
                </form>
            )}
        </div>
    );
};

export default OffertoSignPlayer;










